import clsx from 'clsx';
import React, { useRef, useState } from 'react';
import { Trash2, X } from 'react-feather';
import { useFieldArray, useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { GenerateLabelsDto } from '../interfaces/generate-labels.dto';
import { Stock } from '../interfaces/stock';
import { api } from '../services/api';
import { downloadPdf } from '../services/helpers';
import myToastr from '../services/toastr';

interface LabelItem {
  productId: number;
  sku: string;
  name: string;
  quantity: number;
}

interface GenerateLabelsForm {
  products: LabelItem[];
}

interface Props {
  show: boolean;
  storeId: number;
  close: () => void;
}

const GenerateLabelsModal = (props: Props) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Stock[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<GenerateLabelsForm>({
    mode: 'onSubmit',
    defaultValues: { products: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
  });

  const close = () => {
    setSearchQuery('');
    setSearchResults([]);
    setGenerating(false);
    setSearching(false);
    reset({ products: [] });
    props.close();
  };

  const searchStock = (query: string) => {
    setSearchQuery(query);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (!query || query.trim().length === 0) {
      setSearchResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        setSearching(true);
        const results: Stock[] = await api.getPosStock(props.storeId, undefined, undefined, query.trim());
        setSearchResults(results);
      } catch (e) {
        myToastr.error('Error buscando productos');
      } finally {
        setSearching(false);
      }
    }, 500);
  };

  const addProduct = (stock: Stock) => {
    if (fields.some((item: LabelItem) => item.productId === stock.productId)) {
      myToastr.error('El producto ya está en la lista');
      return;
    }
    append({
      productId: stock.productId,
      sku: stock.product?.sku || '',
      name: stock.product?.name || '',
      quantity: 1,
    });
    setSearchQuery('');
    setSearchResults([]);
  };

  const onSubmit = async (data: GenerateLabelsForm) => {
    if (generating) {
      return;
    }
    try {
      setGenerating(true);
      myToastr.info('Generando etiquetas. Espere por favor.');
      const generateLabelsDto: GenerateLabelsDto = {
        storeIds: [props.storeId],
        products: data.products.map((item: LabelItem) => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
      };
      const result = await api.getProductsLabels(generateLabelsDto);
      downloadPdf(result, 'etiquetas-productos.pdf');
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } catch (e) {
      myToastr.error('Hubo un error generando las etiquetas');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal className="vercomi-modal my-form generate-labels-modal" isOpen={props.show} onRequestClose={close} shouldCloseOnOverlayClick={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="content">
          <div className="d-flex justify-content-between mb-3">
            <div className="title">Generar etiquetas</div>
            <button type="button" className="close-button-modal" onClick={close} title="Cerrar">
              <X size={16} />
            </button>
          </div>
          <div className="generate-labels-search-container mb-3">
            <div className="input-name">Buscar producto (SKU, EAN o nombre)</div>
            <input
              className="input-filter w-100"
              type="text"
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => searchStock(e.target.value)}
              placeholder="Escribe para buscar..."
            />
            {searchResults.length > 0 && (
              <div className="generate-labels-dropdown">
                {searchResults.map((stock: Stock) => (
                  <div key={stock.productId} className="generate-labels-dropdown-item d-flex justify-content-between align-items-center px-3 py-2 cursor-pointer" onClick={() => addProduct(stock)}>
                    <div>
                      <strong>{stock.product?.sku}</strong> - {stock.product?.name}
                    </div>
                    <span style={{ color: '#888', fontSize: '12px' }}>Stock: {stock.quantity}</span>
                  </div>
                ))}
              </div>
            )}
            {searching && <span style={{ fontSize: '12px', color: '#888' }}>Buscando...</span>}
          </div>
          {fields.length > 0 && (
            <div className="generate-labels-table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Nombre</th>
                    <th style={{ width: '120px' }}>Cantidad</th>
                    <th style={{ width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((field, index: number) => (
                    <tr key={field.id}>
                      <td>{field.sku}</td>
                      <td>{field.name}</td>
                      <td>
                        <input
                          type="number"
                          className={clsx('input-filter w-100', { error: errors?.products?.[index]?.quantity })}
                          {...register(`products.${index}.quantity`, { required: true, min: 1, valueAsNumber: true })}
                        />
                      </td>
                      <td>
                        <button type="button" className="close-button-modal" onClick={() => remove(index)} title="Eliminar">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {fields.length === 0 && <p style={{ color: '#888', textAlign: 'center' }}>Busca y añade productos para generar etiquetas</p>}
        </div>
        <button className="save-button" type="submit" disabled={generating || fields.length === 0}>
          {generating ? 'Generando...' : 'Generar etiquetas'}
        </button>
      </form>
    </Modal>
  );
};

export default GenerateLabelsModal;
